import React, { useState } from 'react';
import axios from 'axios';
import OTPVerification from './OTPVerification';
import { API_CONFIG } from '../config/api';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const inputStyle = {
  width: '100%', padding: '11px 14px',
  background: '#f8fafc',
  border: '1px solid rgba(15,23,42,0.14)',
  borderRadius: 10, color: '#0f172a', fontSize: '0.9rem',
  outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s',
};
const labelStyle = {
  display: 'block', color: 'rgba(15,23,42,0.65)',
  fontSize: '0.8rem', fontWeight: 500, marginBottom: 6,
};

const ForgotPassword = ({ onBackToLogin }) => {
  const [step, setStep] = useState('email'); // 'email' | 'otp' | 'reset'
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const API_BASE = API_CONFIG.API_BASE;

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }
    try {
      const res = await axios.post(`${API_BASE}/auth/forgot-password`, { email });
      if (res.data.success) {
        setStep('otp');
        setSuccess('Verification code sent to your email');
        if (res.data.otp) {
          console.log('DEBUG OTP:', res.data.otp);
          alert(`DEBUG MODE: Your OTP is: ${res.data.otp}`);
        }
      } else {
        setError(res.data.message || 'Failed to send verification code');
      }
    } catch (err) {
      if (err.response?.status === 404) setError('No account found with this email address');
      else setError(err.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPVerified = () => {
    setStep('reset');
    setError('');
    setSuccess('Email verified! Now set your new password');
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    if (newPassword.length < 6) { setError('Password must be at least 6 characters'); setLoading(false); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); setLoading(false); return; }
    try {
      const res = await axios.post(`${API_BASE}/auth/reset-password`, { email, new_password: newPassword });
      if (res.data.success) {
        setSuccess('Password reset successfully! Redirecting to login…');
        setTimeout(() => onBackToLogin(), 2000);
      } else {
        setError(res.data.message || 'Failed to reset password');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // Shared page shell
  const Shell = ({ children }) => (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(160deg, #ffffff 0%, #eafaf4 55%, #e3f6ee 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
      {/* Glow orbs — identical to Login */}
      <div style={{ position: 'absolute', width: 700, height: 700, borderRadius: '50%', background: 'rgba(124,58,237,0.14)', filter: 'blur(100px)', top: -200, left: -200, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', width: 500, height: 500, borderRadius: '50%', background: 'rgba(59,130,246,0.10)', filter: 'blur(80px)', bottom: -100, right: -100, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', width: 300, height: 300, borderRadius: '50%', background: 'rgba(139,92,246,0.10)', filter: 'blur(60px)', top: '50%', left: '40%', pointerEvents: 'none' }} />
      {/* Grid overlay */}
      <div style={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'linear-gradient(rgba(15,23,42,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.7) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
      <div style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: 400, padding: '0 16px', boxSizing: 'border-box' }}>
        {children}
      </div>
    </div>
  );

  // OTP step — wrap OTPVerification in the same shell
  if (step === 'otp') {
    return (
      <Shell>
        <OTPVerification
          email={email}
          username=""
          onVerificationComplete={handleOTPVerified}
          onCancel={() => setStep('email')}
          title="Password Reset Verification"
          subtitle={`Enter the verification code sent to ${email}`}
        />
      </Shell>
    );
  }

  return (
    <Shell>
      <div style={{
        background: 'rgba(255,255,255,0.8)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        border: '1px solid rgba(15,23,42,0.08)',
        borderRadius: 20,
        padding: '36px 32px',
        boxShadow: '0 32px 80px rgba(15,23,42,0.12), 0 0 0 1px rgba(124,58,237,0.06)',
      }}>

        {/* Logo — same as Login left panel */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 }}>
          <img src="/cognixaihub_new_logo.png" alt="CognixAI Hub" style={{ width: 114, height: 38, objectFit: 'contain' }} />
        </div>

        <h2 style={{ color: '#0f172a', fontSize: '1.4rem', fontWeight: 700, textAlign: 'center', marginBottom: 6 }}>
          {step === 'email' ? 'Forgot Password' : 'Reset Password'}
        </h2>
        <p style={{ color: 'rgba(15,23,42,0.5)', fontSize: '0.82rem', textAlign: 'center', marginBottom: 24 }}>
          {step === 'email'
            ? "Enter your email and we'll send a verification code"
            : 'Enter your new password below'}
        </p>

        {/* Back to Login */}
        <button
          onClick={onBackToLogin}
          style={{ background: 'none', border: 'none', color: '#7c3aed', fontSize: '0.8rem', cursor: 'pointer', padding: 0, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 4 }}
        >
          ← Back to Login
        </button>

        {step === 'email' ? (
          <form onSubmit={handleEmailSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={labelStyle}>Email Address</label>
              <input
                type="email" value={email} required
                onChange={e => setEmail(e.target.value)}
                placeholder="Enter your email address"
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = 'rgba(124,58,237,0.6)'}
                onBlur={e => e.target.style.borderColor = 'rgba(15,23,42,0.14)'}
              />
            </div>

            {error && <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: '0.82rem' }}>{error}</div>}
            {success && <div style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 8, padding: '10px 14px', color: '#059669', fontSize: '0.82rem' }}>{success}</div>}

            <button
              type="submit" disabled={loading}
              style={{ width: '100%', padding: '12px', borderRadius: 10, border: 'none', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.92rem', color: '#fff', background: loading ? 'rgba(139,92,246,0.4)' : 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)', boxShadow: loading ? 'none' : '0 4px 20px rgba(124,58,237,0.3)', transition: 'all 0.2s' }}
            >
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                  Sending Code…
                </span>
              ) : 'Send Verification Code'}
            </button>
          </form>
        ) : (
          <form onSubmit={handlePasswordReset} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={labelStyle}>New Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showNew ? 'text' : 'password'} value={newPassword} required
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  style={{ ...inputStyle, paddingRight: 42 }}
                  onFocus={e => e.target.style.borderColor = 'rgba(124,58,237,0.6)'}
                  onBlur={e => e.target.style.borderColor = 'rgba(15,23,42,0.14)'}
                />
                <button type="button" onClick={() => setShowNew(p => !p)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(15,23,42,0.4)', padding: 0 }}>
                  {showNew ? <EyeSlashIcon style={{ width: 18, height: 18 }} /> : <EyeIcon style={{ width: 18, height: 18 }} />}
                </button>
              </div>
            </div>

            <div>
              <label style={labelStyle}>Confirm New Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirm ? 'text' : 'password'} value={confirmPassword} required
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  style={{ ...inputStyle, paddingRight: 42 }}
                  onFocus={e => e.target.style.borderColor = 'rgba(124,58,237,0.6)'}
                  onBlur={e => e.target.style.borderColor = 'rgba(15,23,42,0.14)'}
                />
                <button type="button" onClick={() => setShowConfirm(p => !p)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(15,23,42,0.4)', padding: 0 }}>
                  {showConfirm ? <EyeSlashIcon style={{ width: 18, height: 18 }} /> : <EyeIcon style={{ width: 18, height: 18 }} />}
                </button>
              </div>
            </div>

            {error && <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: '0.82rem' }}>{error}</div>}
            {success && <div style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 8, padding: '10px 14px', color: '#059669', fontSize: '0.82rem' }}>{success}</div>}

            <button
              type="submit" disabled={loading}
              style={{ width: '100%', padding: '12px', borderRadius: 10, border: 'none', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.92rem', color: '#fff', background: loading ? 'rgba(139,92,246,0.4)' : 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)', boxShadow: loading ? 'none' : '0 4px 20px rgba(124,58,237,0.3)', transition: 'all 0.2s' }}
            >
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                  Resetting Password…
                </span>
              ) : 'Reset Password'}
            </button>
          </form>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </Shell>
  );
};

export default ForgotPassword;
